import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { Method, Service, autheidServiceUrl, environment } from 'shared-components/configuration';
import useSWQuery from 'shared-components/hooks/useSWQuery';
import { BaseProps } from 'shared-components/utils/types';

import { useApp } from './AppProvider';

type AuthEIDContextProps = {
  selectorInitExecute: () => void;
  selectorInitStop: () => void;
  resetCounter: () => void;
  logout: () => void;
  expire: number;
  token: string;
  created: string;
  requestId: string;
  isAdmin: number;
  lockTimeout: boolean;
  requestCounter: number;
};
let lockTimeoutPointer: string | number | NodeJS.Timeout | undefined;
let noQrTimeoutPointer: string | number | NodeJS.Timeout | undefined;

const AuthEIDContext = createContext<AuthEIDContextProps>({
  selectorInitExecute: function (): void {
    throw new Error('Function not implemented.');
  },
  selectorInitStop: function (): void {
    throw new Error('Function not implemented.');
  },
  resetCounter: function (): void {
    throw new Error('Function not implemented.');
  },
  logout: function (): void {
    throw new Error('Function not implemented.');
  },

  expire: 0,
  token: '',
  created: '',
  requestId: '',
  isAdmin: 0,
  lockTimeout: false,
  requestCounter: 0
});

export const useAuthEID = () => {
  const context = useContext(AuthEIDContext);
  if (!context) {
    throw new Error('GatewayContext not available');
  }
  return context;
};

const AuthEIDProvider = ({ children }: BaseProps) => {
  const {
    values: { serviceWorkerReady },
    setters: { setNeedAuth }
  } = useApp();

  const [needRenew, setNeedRenew] = useState<boolean>(false);
  const [stop, setStop] = useState(false);
  const [expire, setExpire] = useState(-1);
  const [token, setToken] = useState('');
  const [created, setCreated] = useState('');
  const [requestId, setRequestId] = useState('');
  const [lockTimeout, setLockTimeout] = useState<boolean>(false);
  const [requestCounter, setRequestCounter] = useState<number>(0);

  const selectorInit = useSWQuery({
    service: Service.AUTHEID,
    method: Method.QUERY,
    returnObjectName: 'selector',
    data: {
      api: 'selector',
      method: 'selector_init',
      args: {
        service_url: autheidServiceUrl
      }
    },
    auto: false,
    onResponse: (data: any) => {
      const responseRequestId = data.data.request_id;
      const status = data.data.status;
      if (responseRequestId !== undefined && responseRequestId?.length > 0) {
        setRequestId(responseRequestId);
        setRequestCounter((requestCounter) => requestCounter + 1);
      }
      switch (status) {
        case 'SUCCESS': {
          //          setExpire(100);
          setExpire(data.data.expires_in);
          setToken(data.data.access_token);
          setCreated(data.data.account_created);
          setStop(true);
          setNeedAuth(true);
          setNeedRenew(true);
          break;
        }
        case 'TIMEOUT': {
          if (stop === false && lockTimeout === false) {
            selectorInitExecute();
          }
          break;
        }
      }
    }
  });

  const _stopLoginRenew = useSWQuery({
    service: Service.AUTHEID,
    method: Method.STOP_TOKEN_RENEW,
    returnObjectName: 'nonexistent',
    data: null,
    auto: false
  });

  const onTimeoutCallback = useCallback(() => {
    if (requestId.length === 0) {
      setLockTimeout(false);
      clearTimeout(lockTimeoutPointer);
    }
  }, [requestId]);

  const selectorInitExecute = useCallback(() => {
    if (requestCounter > 3) {
      return;
    }
    if (lockTimeout === false) {
      setStop(false);
      selectorInit.execute();
      lockTimeoutPointer = setTimeout(() => {
        setLockTimeout(false);
      }, 57_000);
      setLockTimeout(true);
      noQrTimeoutPointer = setTimeout(onTimeoutCallback, 5000);
    }
  }, [lockTimeout, onTimeoutCallback, requestCounter, selectorInit]);

  const selectorInitStop = useCallback(() => {
    setStop(true);
  }, [setStop]);

  const logout = useCallback(() => {
    setStop(true);
    setExpire(-1);
    setToken('');
    setCreated('');
    setRequestId('');
  }, [setCreated, setExpire, setStop, setToken]);

  const resetCounter = useCallback(() => {
    setLockTimeout(false);
    setStop(true);
    setExpire(-1);
    setToken('');
    setCreated('');
    setRequestId('');
    setRequestCounter(0);
  }, []);

  useEffect(() => {
    if (serviceWorkerReady === false) {
      return;
    }
    if (expire === undefined || expire === -1) {
      setStop(false);
    } else {
      setStop(true);
      if (needRenew) {
        /*
        loginRenew.execute({
          data: {
            args: {
              access_token: token
            },
            timer: expire
          }
        });
        */
        setNeedRenew(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expire, needRenew]);
  useEffect(() => {
    if (requestId.length > 0) {
      clearTimeout(noQrTimeoutPointer);
      noQrTimeoutPointer = undefined;
    }
  }, [requestId]);
  return (
    <AuthEIDContext.Provider
      value={{
        selectorInitStop,
        selectorInitExecute,
        resetCounter,
        logout,
        lockTimeout,
        expire,
        token,
        created,
        requestId,
        requestCounter,
        isAdmin: environment.includes('admin') ? 1 : 0
      }}
    >
      {children}
    </AuthEIDContext.Provider>
  );
};

export default AuthEIDProvider;
